import { ProposalLog, ThreadActivityDataProposal } from './proposal'

// What a tension shows under itself once a proposal has been approved
// (REU13-D2): the proposal's own title, then the changes it applied.
export interface ApprovedProposal {
  title: string
  /**
   * May be **empty** since REU14-D7: an approved proposal that changed nothing
   * is still a decision taken, and it is announced under its title rather than
   * hidden. Callers must handle the empty case — screen and email both write a
   * "no org chart change" line there.
   */
  logs: ProposalLog[]
}

// The screen needs one more thing than the email: a stable React key.
export interface ThreadApprovedProposal extends ApprovedProposal {
  activityId: string
}

// The activity shape the rule needs, and nothing more — so it can be asserted
// without a GraphQL result at hand.
interface ProposalActivity {
  data: unknown
}

// What grouping needs on top of the rule.
interface ThreadProposalActivity extends ProposalActivity {
  id: string
  threadId: string
}

/**
 * The approved proposals of ONE tension, in order.
 *
 * 🔑 The unit is the **proposal**, never the change (REU14-D1): the screen and
 * the compte rendu email both read this rule, so a tension cannot be described
 * one way in the app and another way in someone's inbox — the very divergence
 * REU-12 already cost us, and the reason the rule now lives here instead of
 * being written once per package.
 *
 * Only **approved** ones count (REU13-D3): a refused or still-running proposal
 * prepared changes that were never applied, and showing them would state the
 * opposite of what happened.
 *
 * A proposal that changed nothing is **kept** (REU14-D7), with empty `logs`.
 * It used to be dropped (REU13-D7), a rule taken back when the unit displayed
 * was the *change*: a block without changes was then literally empty. Now that
 * the unit is the proposal, an approved proposal that touched no role is not a
 * void — it is a decision taken, for instance one that settles a policy.
 *
 * A proposal without a title is dropped, because the title is what announces
 * it: an empty heading followed by "no org chart change" would read as a bug.
 * No such row exists in the database (REU14-D12) — this guards the years-old
 * rows saved before the field existed, nothing more.
 *
 * Order is the activities' own, so several approved proposals read in the
 * order they were decided (REU13-D8).
 */
export function readApprovedProposals(
  activities: ProposalActivity[]
): ApprovedProposal[] {
  const proposals: ApprovedProposal[] = []

  for (const activity of activities) {
    const data = activity.data as ThreadActivityDataProposal | null
    if (data?.status !== 'approved') continue
    if (!data.title) continue

    proposals.push({ title: data.title, logs: data.logs ?? [] })
  }

  return proposals
}

/**
 * The same proposals, grouped by tension — what a meeting step needs when it
 * loads every tension at once.
 *
 * 🔑 Only the screen goes through here. The email walks tension by tension and
 * calls `readApprovedProposals` directly, because its GraphQL query selects
 * neither `id` nor `threadId` on the activity — and adding them would mean a
 * codegen this feature deliberately does not need (REU14-D5).
 */
export function groupApprovedProposals(
  activities: ThreadProposalActivity[]
): Map<string, ThreadApprovedProposal[]> {
  const byThread = new Map<string, ThreadApprovedProposal[]>()

  for (const activity of activities) {
    const [proposal] = readApprovedProposals([activity])
    if (!proposal) continue

    const list = byThread.get(activity.threadId) ?? []
    list.push({ activityId: activity.id, ...proposal })
    byThread.set(activity.threadId, list)
  }

  return byThread
}
